// src/pages/Dashboard/StatsBox.js
import React from "react";
import useChatbotStats from "./useChatbotStats";
import {
  CircularProgress,
  Box,
  Typography,
  Grid,
  Chip,
} from "@mui/material";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TokenIcon from '@mui/icons-material/Token';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
};

export default function StatsBox() {
  const { stats, error, loading } = useChatbotStats();

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: 'center', p: 4 }}>
        <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
        <Typography sx={{ ml: 2, fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
          Loading stats...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3, bgcolor: 'rgba(239,68,68,0.1)', borderRadius: '12px' }}>
        <Typography sx={{ fontFamily, color: '#dc2626' }}>{error}</Typography>
      </Box>
    );

  if (!stats)
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily, color: colors.textSecondary }}>
          No stats available for this chatbot.
        </Typography>
      </Box>
    );

  const statCards = [
    {
      title: 'Number of Files',
      value: stats.privateDocs.total_documents,
      icon: <DescriptionOutlinedIcon />,
      color: '#3b82f6',
      chips: [
        { label: `${stats.privateDocs.total_page_characters} chars` },
        { label: `${stats.privateDocs.total_page_words} words` },
        { label: `${stats.privateDocs.total_tokens} tokens` },
      ],
    },
    {
      title: 'Website Links',
      value: stats.websiteLinks.total_links,
      icon: <LinkIcon />,
      color: '#10b981',
      chips: [
        { label: `${stats.websiteLinks.total_page_characters} chars` },
        { label: `${stats.websiteLinks.total_page_words} words` },
        { label: `${stats.websiteLinks.total_tokens} tokens` },
      ],
    },
    {
      title: 'Total Words',
      value: stats.totalWords,
      icon: <TextFieldsIcon />,
      color: '#8b5cf6',
      chips: [
        { label: `Chars: ${stats.totalCharacters}` },
        { label: `Tokens: ${stats.totalTokens}` },
      ],
    },
    {
      title: 'Used Tokens',
      value: stats.tokens.total_total_token,
      icon: <TokenIcon />,
      color: '#f59e0b',
      chips: [
        { label: `Input: ${stats.tokens.total_input_tokens}` },
        { label: `Output: ${stats.tokens.total_output_tokens}` },
      ],
    },
  ];

  return (
    <Grid container spacing={2}>
      {statCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Box sx={{
            p: 2.5,
            borderRadius: '14px',
            bgcolor: 'rgba(255,255,255,0.6)',
            border: '1px solid rgba(0,0,0,0.04)',
            height: '100%',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography sx={{
                fontFamily,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: colors.textSecondary,
              }}>
                {card.title}
              </Typography>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: `${card.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.color,
              }}>
                {card.icon}
              </Box>
            </Box>
            <Typography sx={{
              fontFamily,
              fontSize: '1.75rem',
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 1.5,
            }}>
              {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {card.chips.map((chip, chipIndex) => (
                <Chip
                  key={chipIndex}
                  size="small"
                  label={chip.label}
                  sx={{
                    fontFamily,
                    fontSize: '0.65rem',
                    height: 22,
                    bgcolor: `${card.color}10`,
                    color: card.color,
                    fontWeight: 500,
                    border: 'none',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}
